import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Download, Eye, Mail, Send, ShieldCheck, Clock, Archive, Search, X, Lock } from "lucide-react";

export default function PayStubs() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stubs, setStubs] = useState([]);
  const [busy, setBusy] = useState(null); // stub id currently mid-action
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selfService, setSelfService] = useState(null); // null = flag not resolved yet

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      params.set("limit", "2000");
      const { data } = await api.get(`/paystubs?${params.toString()}`);
      setStubs(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [q, dateFrom, dateTo]);

  // Wait for the flag before fetching: an employee must never see a flash of
  // the unlocked layout while /config/features is in flight.
  useEffect(() => {
    if (selfService === null) return;
    if (!isAdmin && !selfService) { setLoading(false); return; }
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load, selfService, isAdmin]);

  useEffect(() => {
    api.get("/config/features")
      .then(({ data }) => setSelfService(!!data.employee_stub_download))
      .catch((err) => { console.warn("feature flags unavailable", err); setSelfService(false); });
  }, []);

  // Every hook must run before the loading early-return below.
  const totals = useMemo(() => stubs.reduce((a, s) => ({
    gross: a.gross + (s.gross || 0),
    taxes: a.taxes + (s.total_deductions || 0),
    net: a.net + (s.net_pay || 0),
  }), { gross: 0, taxes: 0, net: 0 }), [stubs]);

  if (selfService === null) {
    return <div className="p-10 font-mono text-sm" data-testid="stubs-loading">LOADING…</div>;
  }

  const clearFilters = () => { setQ(""); setDateFrom(""); setDateTo(""); };
  const filtered = q.trim() || dateFrom || dateTo;

  const releaseStub = async (id) => {
    setBusy(`release-${id}`);
    try {
      const { data } = await api.post(`/paystubs/${id}/release`);
      toast.success(data.emailed ? "Released + emailed" : "Released (email skipped)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const resendEmail = async (id) => {
    setBusy(`email-${id}`);
    try {
      const { data } = await api.post(`/paystubs/${id}/resend-email`);
      if (data.ok) toast.success("Email sent");
      else toast.error("Email failed — check server logs");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  if (!isAdmin && !selfService) {
    return (
      <div className="p-10">
        <div className="overline mb-2">§ PAY STUBS</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mb-6">Pay Stubs</h1>
        <div className="hard-card p-6 max-w-2xl" data-testid="stub-self-service-locked">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <div className="font-heading font-black text-base md:text-lg">Issued by your administrator</div>
              <p className="text-sm mt-2 text-muted-foreground">
                Pay stub history and PDF downloads are handled by your payroll administrator.
                You will receive each stub by email as soon as it is released. Ask your admin
                if you need a copy of an earlier period.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-10">
      <div className="overline mb-2">§ PAY STUBS · HISTORICAL LEDGER</div>
      <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mb-2">Pay Stubs</h1>
      <p className="text-muted-foreground mb-8">
        Every stub ever issued for this company. Search by employee and narrow by pay date.
      </p>

      <div className="hard-card p-4 mb-6 flex flex-wrap items-end gap-4" data-testid="stub-filters">
        <label className="flex-1 min-w-[220px]">
          <span className="overline block mb-1">Employee / check #</span>
          <div className="flex items-center gap-2 border-2 border-ink bg-white px-3 py-2">
            <Search className="w-4 h-4" />
            <input
              data-testid="stub-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. Rivera"
              className="font-mono text-xs w-full outline-none bg-transparent"
            />
          </div>
        </label>
        <label>
          <span className="overline block mb-1">Pay date from</span>
          <input data-testid="stub-date-from" type="date" value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border-2 border-ink bg-white px-3 py-2 font-mono text-xs" />
        </label>
        <label>
          <span className="overline block mb-1">Pay date to</span>
          <input data-testid="stub-date-to" type="date" value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border-2 border-ink bg-white px-3 py-2 font-mono text-xs" />
        </label>
        {filtered && (
          <button data-testid="stub-clear-filters" onClick={clearFilters}
            className="border-2 border-ink px-3 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-1 hover:bg-ink hover:text-white transition-colors">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        <div className="font-mono text-xs ml-auto" data-testid="stub-result-count">
          {loading ? "SEARCHING…" : `${stubs.length} stub(s)`}
          {!loading && stubs.length > 0 && (
            <> · gross {fmtMoney(totals.gross)} · taxes {fmtMoney(totals.taxes)} · net {fmtMoney(totals.net)}</>
          )}
        </div>
      </div>

      <div className="hard-card overflow-x-auto">
        <table className="w-full font-mono text-sm" data-testid="stubs-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Pay Date</th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Employee</th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Period</th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Check #</th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Status</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Gross</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Net</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody>
            {stubs.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-muted-foreground" data-testid="stubs-empty">
                {loading ? "Loading…" : filtered ? "No pay stubs match this search." : "No pay stubs yet."}
              </td></tr>
            )}
            {stubs.map((s, i) => {
              const released = !!s.released_at;
              const backfill = !!s.is_backfill;
              return (
                <tr key={s.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`stub-row-${s.id}`}>
                  <td className="px-4 py-3 border-t border-ink/20 font-semibold">{s.pay_date}</td>
                  <td className="px-4 py-3 border-t border-ink/20">{s.employee_snapshot?.first_name} {s.employee_snapshot?.last_name}</td>
                  <td className="px-4 py-3 border-t border-ink/20">{s.period_start} → {s.period_end}</td>
                  <td className="px-4 py-3 border-t border-ink/20">{s.check_number}</td>
                  <td className="px-4 py-3 border-t border-ink/20 whitespace-nowrap">
                    {released ? (
                      <span data-testid={`stub-released-${s.id}`} className="inline-flex items-center gap-1 border-2 border-good bg-good/10 text-good px-2 py-0.5 text-[10px] uppercase tracking-widest">
                        <ShieldCheck className="w-3 h-3" /> Released
                      </span>
                    ) : (
                      <span data-testid={`stub-hidden-${s.id}`} className="inline-flex items-center gap-1 border-2 border-warn bg-warn/10 text-ink px-2 py-0.5 text-[10px] uppercase tracking-widest">
                        <Clock className="w-3 h-3" /> Hidden
                      </span>
                    )}
                    {backfill && (
                      <span data-testid={`stub-backfill-${s.id}`} className="ml-1 inline-flex items-center gap-1 border-2 border-ink bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-widest">
                        <Archive className="w-3 h-3" /> Backfill
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 border-t border-ink/20 text-right">{fmtMoney(s.gross)}</td>
                  <td className="px-4 py-3 border-t border-ink/20 text-right font-black">{fmtMoney(s.net_pay)}</td>
                  <td className="px-4 py-3 border-t border-ink/20 text-right space-x-1 whitespace-nowrap">
                    <Link to={`/app/paystubs/${s.id}`} className="inline-flex items-center gap-1 border border-ink px-2 py-1 text-xs uppercase tracking-widest hover:bg-ink hover:text-white"><Eye className="w-3 h-3" /> View</Link>
                    <a data-testid={`download-pdf-${s.id}`} href={`${API}/paystubs/${s.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 border-2 border-ink px-2 py-1 text-xs uppercase tracking-widest hover:bg-ink hover:text-white">
                      <Download className="w-3 h-3" /> PDF
                    </a>
                    {isAdmin && !released && (
                      <button
                        data-testid={`release-stub-${s.id}`}
                        onClick={() => releaseStub(s.id)}
                        disabled={busy === `release-${s.id}`}
                        className="inline-flex items-center gap-1 bg-ink text-white px-2 py-1 text-xs uppercase tracking-widest hover:bg-good disabled:opacity-40"
                        title="Release to employee portal + email"
                      ><Send className="w-3 h-3" /> {busy === `release-${s.id}` ? "…" : "Release"}</button>
                    )}
                    {isAdmin && released && (
                      <button
                        data-testid={`send-email-${s.id}`}
                        onClick={() => resendEmail(s.id)}
                        disabled={busy === `email-${s.id}`}
                        className="inline-flex items-center gap-1 border-2 border-ink px-2 py-1 text-xs uppercase tracking-widest hover:bg-ink hover:text-white disabled:opacity-40"
                        title="Send the stub email (again)"
                      ><Mail className="w-3 h-3" /> {busy === `email-${s.id}` ? "…" : "Send Email"}</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
